import {
  Center,
  HStack,
  Progress,
  Spacer,
  Tag,
  Text,
  useColorModeValue,
  useDisclosure,
  VStack,
  Flex,
} from '@chakra-ui/react';
import React from 'react';

import { finleyAssert } from '@finley-technologies/common';
import { DataPipeline } from '@finley-technologies/finley-types';

import { EarlierVersionFile } from './EarlierVersionFile';
import { ReceivableReconciliation } from './ReceivableReconciliation/ReceivableReconciliation';
import { colors } from '~/packages/common-frontend/colors';
import { InfoIconButton } from '~/packages/common-frontend/components/IconButton';
import { useIsNeededInput } from '~/packages/dataPipeline/hooks/neededInputContext';
import { useSlugToName } from '~/packages/dataPipeline/hooks/slugToNameContext';
import {
  useUploadContextMeshDocument,
  useUploadContextMeshRunParams,
} from '~/packages/dataPipeline/hooks/UploadContext';
import { useInputNode } from '~/packages/dataPipeline/hooks/useInputNode';
import { DataDictionaryModal } from '~/packages/dataPipeline/ui/dataDictionary/DataDictionary';
import { DownloadInstanceIconButton } from '~/packages/dataPipeline/ui/details/DownloadMenuButton';
import { UploadStatusIndicator } from '~/packages/dataPipeline/ui/uploads/pipelinePicker/UploadStateIndicator';
import { UploadModal } from '~/packages/dataPipeline/ui/uploads/UploadModal';
import { UploadCashBalanceForm } from '~/packages/manualDataTapeUploads/ui/UploadCashBalanceForm';
import { UploadDataTapeForm } from '~/packages/manualDataTapeUploads/ui/UploadDataTapeForm';

export const UploadSingleInput = ({
  organizationId,
  meshInput,
}: {
  organizationId: string;
  meshInput: DataPipeline.MeshInput;
}) => {
  const meshRunParams = useUploadContextMeshRunParams();
  const meshDocument = useUploadContextMeshDocument();

  const {
    rdfv: { reportingDate, fileVersion },
  } = meshRunParams;
  const { borrowerOrganizationId, implementationId } = meshDocument;

  const {
    upload,
    keep,
    uploadNeeded,
    canRetry,
    progress,
    instanceState,
    cashUploadStarted,
    cashUploadEnded,
    cashUploadFailed,
    errorMessage,
    sanitized,
    downloadableInstanceId,
  } = useInputNode({
    artifactId: meshInput.artifactId,
  });

  const currentArtifactId = meshInput.artifactId;
  const currentArtifact = meshDocument.artifacts.find((artifact) => {
    return artifact.id === currentArtifactId;
  });
  finleyAssert(
    currentArtifact,
    'Expected to find current artifact in mesh document',
  );

  const isNeeded = useIsNeededInput({ meshInput, artifact: currentArtifact });

  const uploadModal = useDisclosure();
  const dataTapeFormatModal = useDisclosure();
  const { cfSlugToName, lpSlugToName } = useSlugToName();

  const textColor = useColorModeValue(colors.black[70], colors.white[700]);
  const backgroundColor = useColorModeValue(
    colors.background[50],
    colors.gray[700],
  );
  const borderColor = useColorModeValue(colors.gray[100], '#ffffff29');
  const errorTextColor = useColorModeValue('red.600', 'red.300');

  const mainText = meshInput.friendlyName;
  const otherTexts: string[] = [];
  if (
    meshInput.creditFacilitySlug &&
    // we do not want to render CFSlug for valley bank inputs.
    meshInput.creditFacilitySlug !== 'valleyBankBorrowersValleyBank1'
  ) {
    otherTexts.push(cfSlugToName(meshInput.creditFacilitySlug));
  }
  if ('loanProductSlug' in meshInput && meshInput.loanProductSlug) {
    otherTexts.push(lpSlugToName(meshInput.loanProductSlug));
  }

  return (
    <Flex direction="column" width="100%">
      <VStack
        key={meshInput.artifactId}
        as="span"
        alignItems="flex-start"
        spacing={1}
        borderRadius="8px"
        padding="16px"
        border={`1px solid ${isNeeded ? colors.blue[400] : borderColor}`}
        bg={backgroundColor}
      >
        <HStack width="100%" justifyContent="space-between">
          <Text
            as="span"
            color={textColor}
            fontSize="md"
            fontWeight="bold"
            lineHeight="1.2rem"
          >
            {mainText}
          </Text>
          {meshInput.type === 'file' && meshInput.dataDictionary.length > 0 && (
            <>
              <DataDictionaryModal
                isOpen={dataTapeFormatModal.isOpen}
                onClose={dataTapeFormatModal.onClose}
                dataDictionary={meshInput.dataDictionary}
              />
              <InfoIconButton
                ariaLabel="Data Tape Format"
                label="Data Tape Format"
                onClick={dataTapeFormatModal.onOpen}
                fontSize="xl"
                color={colors.blue[400]}
              />
            </>
          )}
          <Spacer />
          {meshInput.type === 'file' && downloadableInstanceId && (
            <DownloadInstanceIconButton
              artifactInstanceId={downloadableInstanceId}
              label={mainText}
            />
          )}
          <UploadStatusIndicator
            status={instanceState}
            sanitized={sanitized}
            onClick={uploadNeeded ? uploadModal.onOpen : null}
            canRetry={canRetry}
          />
          {meshInput.type === 'file' && (
            <UploadModal
              modalHeaderText="Upload Tape"
              isOpen={uploadModal.isOpen}
              onClose={uploadModal.onClose}
            >
              <UploadDataTapeForm
                allowedExtensions={[meshInput.extension]}
                onClose={uploadModal.onClose}
                handleFile={(file) => upload(file)}
              />
              <EarlierVersionFile
                implementationId={implementationId}
                reportingDate={reportingDate}
                fileVersion={fileVersion}
                artifactSlug={currentArtifact.artifactSlug}
                onKeep={(fromFileVersion) => keep(fromFileVersion)}
                onClose={uploadModal.onClose}
              />
            </UploadModal>
          )}
          {meshInput.type === 'cashBalance' && (
            <UploadModal
              modalHeaderText="Upload Cash Balance"
              isOpen={uploadModal.isOpen}
              onClose={uploadModal.onClose}
            >
              <Text fontWeight="bold" mb="1rem" fontSize="xl">
                {cfSlugToName(meshInput.creditFacilitySlug)}
              </Text>
              <UploadCashBalanceForm
                organizationId={borrowerOrganizationId}
                creditFacilityId={meshInput.creditFacilityId}
                reportingDate={reportingDate}
                fileVersion={fileVersion}
                accept={{
                  'image/png': ['.png'],
                }}
                onClose={uploadModal.onClose}
                onLoading={cashUploadStarted}
                onSuccess={cashUploadEnded}
                onError={cashUploadFailed}
              />
            </UploadModal>
          )}
        </HStack>
        <Text as="span" color={textColor} lineHeight="1.2rem">
          {otherTexts.join(', ')}
        </Text>
        {isNeeded && (
          <Tag size="sm" colorScheme="blue">
            Needed for this deliverable
          </Tag>
        )}
        {errorMessage && (
          <Text as="span" color={errorTextColor} lineHeight="1.2rem">
            {errorMessage}
          </Text>
        )}
        {progress > 0 && (
          <Center w="100%">
            <Progress colorScheme="green" value={progress} size="sm" w="100%" />
          </Center>
        )}
      </VStack>
      {currentArtifact.type === 'rawLoanTape' &&
        meshInput.type === 'file' &&
        meshInput.loanProductSlug &&
        meshInput.creditFacilitySlug &&
        organizationId === borrowerOrganizationId && (
          <ReceivableReconciliation
            loanProductSlug={meshInput.loanProductSlug}
            creditFacilitySlug={meshInput.creditFacilitySlug}
            implementationId={implementationId}
          />
        )}
    </Flex>
  );
};
